import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";

const sortTabs = [
  { value: "default", label: "综合排序" },
  { value: "updated", label: "最新更新" },
  { value: "popular", label: "最热门" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || "";
  const [input, setInput] = useState(keyword);
  const [sort, setSort] = useState("default");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) return [];
    const list = comics.filter(
      (comic) =>
        comic.title.toLowerCase().includes(trimmed) ||
        comic.description.toLowerCase().includes(trimmed) ||
        comic.author.toLowerCase().includes(trimmed),
    );
    return list.sort((a, b) => {
      if (sort === "updated") {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
      if (sort === "popular") {
        return b.views - a.views;
      }
      return b.views - a.views;
    });
  }, [keyword, sort]);

  const hot = useMemo(() => [...comics].sort((a, b) => b.views - a.views).slice(0, 4), []);

  useEffect(() => {
    document.title = keyword
      ? `“${keyword}”的搜索结果 - JMcomic2漫画搜索`
      : "搜索漫画 - JMcomic2高清漫画";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        keyword
          ? `JMcomic2 搜索“${keyword}”，共找到 ${results.length} 部相关高清漫画作品，支持在线阅读与更新提示。`
          : "在 JMcomic2 搜索你想看的高清漫画，支持按标题、简介与作者精准匹配。",
      );
    }
  }, [keyword, results.length]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    const params = new URLSearchParams();
    if (value) params.set("keyword", value);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="w-full px-4 md:px-6 py-8">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-950">搜索结果</span>
      </nav>

      <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mb-5">
        {keyword ? `“${keyword}” 的搜索结果` : "搜索漫画"}
      </h1>

      <form
        onSubmit={submit}
        className="flex items-center gap-2 h-12 px-4 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors max-w-2xl mb-8"
      >
        <i className="ri-search-line text-foreground-500 text-lg" />
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="输入漫画标题、简介或作者"
          className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
        />
        <button
          type="submit"
          className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      {keyword && results.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="text-sm text-foreground-500">
              共找到 <span className="text-primary-500 font-medium">{results.length}</span> 部相关作品
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              {sortTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setSort(tab.value)}
                  className={`px-4 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    sort === tab.value
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {results.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </>
      )}

      {(!keyword || results.length === 0) && (
        <div>
          <div className="flex flex-col items-center justify-center py-16 rounded-lg bg-background-100 border border-background-200">
            <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 mb-4">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <p className="text-foreground-950 font-medium">
              {keyword ? "未找到相关漫画" : "输入关键词开始搜索"}
            </p>
            <p className="mt-1 text-sm text-foreground-500">
              {keyword ? "换个关键词试试，或看看下面的热门推荐" : "支持按标题、简介、作者进行匹配"}
            </p>
          </div>

          <div className="mt-8">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-fire-fill text-primary-500" />
              热门推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {hot.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </div>
      )}

      <p className="mt-8 text-center text-xs text-foreground-500">
        搜索结果均为平台演示数据，仅用于功能展示
      </p>
    </div>
  );
}